import React from 'react'
import { Container, Card, Row, Col } from 'react-bootstrap'
import { capitalizeOnlyFirstLetter, useWindowSize } from '../../../helpers'
import { getCurrentResolutionBreakPoint } from 'utils/utilityFunctions'
import * as S from './proposalElements'
import CarImageSection from 'components/pages/proposal/carImageSection'
import { useSelector } from 'react-redux'
import { Emission } from '@mmsg/louie'
import CarDescriptionBlock from './carDescriptionBlock'
import { IProposalVehicleProps } from './proposalTypes'

interface ICarAttributeProps {
  title?: string | JSX.Element
  value?: string
}

interface IStateProps {
  downloadPdf: { isDownloading: boolean }
}

interface IInputProps {
  vehicle: IProposalVehicleProps
  pdf?: boolean
  title: string
  proposalUpdatedDate: string | null
}

export const CarAttribute = ({ title, value }: ICarAttributeProps) => {
  const isDownloadingPdf = useSelector(
    (state: IStateProps) => state.downloadPdf.isDownloading
  )
  const resolutionBreakpoint = {
    nonDesktop: getCurrentResolutionBreakPoint(isDownloadingPdf),
  }

  const [width] = useWindowSize()
  return (
    <>
      {value && width >= resolutionBreakpoint.nonDesktop && (
        <Row>
          <Col>{title}</Col>
          <Col className="text-right">{value}</Col>
        </Row>
      )}
      {value && width < resolutionBreakpoint.nonDesktop && (
        <>
          <Row>
            <Col>{title}</Col>
          </Row>
          <Row className="mb-3">
            <Col className="font-weight-normal">{value}</Col>
          </Row>
        </>
      )}
    </>
  )
}

const CarDetailsSection = (props: IInputProps) => {
  let engineText = props.vehicle
    ? props.vehicle.engineSize
      ? props.vehicle.engineSize
      : '' + ' ' + props.vehicle.fuelType
      ? props.vehicle.fuelType
      : ''
    : ''

  const emissionAttribute = (
    <div>
      CO<sub>2</sub> emissions
    </div>
  )
  const cO2EmissionsPerKM = props.vehicle && props.vehicle.cO2EmissionsPerKM
  return props.pdf ? (
    <div className="card-section" id="cardetailssection">
      <Container className="section mt-4">
        <Card
          className={props.pdf ? 'mb-13rem pdf-card-container' : 'mb-13rem'}
        >
          <Card.Header
            className={
              props.pdf ? 'border-bottom-0 pdf-card' : 'border-bottom-0'
            }
          >
            <div className="text-header">{props.title}</div>
          </Card.Header>
          <CarDescriptionBlock
            vehicle={props.vehicle}
            vehicleType={props.vehicle && props.vehicle.vehicleType}
          />
          {props.vehicle && props.vehicle.id && (
            <div className="text-center">
              {props.vehicle && props.vehicle.vehicleDescription}
            </div>
          )}
          {props.vehicle && !props.vehicle.id && (
            <div className="text-center">
              {props.vehicle && props.vehicle.vehicleDescription}
            </div>
          )}
          <S.CarBodySection>
            <Card.Body>
              <CarAttribute
                title="Series"
                value={props.vehicle && props.vehicle.variant}
              />
              <CarAttribute
                title="Colour"
                value={props.vehicle && props.vehicle.bodyColour}
              />
              <CarAttribute
                title="Interior colour"
                value={props.vehicle && props.vehicle.trimColour}
              />
              <CarAttribute title="Engine" value={engineText} />
              <CarAttribute
                title="Transmission"
                value={props.vehicle && props.vehicle.transmission}
              />
              <CarAttribute
                title="Body"
                value={props.vehicle && props.vehicle.style}
              />

              <CarAttribute
                title="Fuel type"
                value={capitalizeOnlyFirstLetter(
                  props.vehicle && props.vehicle.fuelType
                )}
              />
              <CarAttribute
                title="Cylinders"
                value={props.vehicle && props.vehicle.vehicleCylinderConfig}
              />
              <CarAttribute
                title="Payload"
                value={
                  props.vehicle && props.vehicle.payLoad == '0'
                    ? undefined
                    : props.vehicle && props.vehicle.payLoad
                }
              />
              {cO2EmissionsPerKM != null && (
                <CarAttribute
                  title={emissionAttribute}
                  value={cO2EmissionsPerKM + ' g/km'}
                />
              )}
            </Card.Body>
            <S.CarImageSection>
              <CarImageSection
                vehicle={props.vehicle}
                proposalUpdatedDate={props.proposalUpdatedDate}
                pdf={true}
              />
            </S.CarImageSection>
          </S.CarBodySection>
          {cO2EmissionsPerKM != null && (
            <S.EmissionContainerPdf>
              <Emission value={cO2EmissionsPerKM} />
            </S.EmissionContainerPdf>
          )}
        </Card>
      </Container>
    </div>
  ) : (
    <div className="card-section" id="cardetailssection">
      <Container className="section mt-4">
        <Card
          className={
            props.pdf ? 'mb-13rem pdf-card-container' : 'mb-13rem card-shadow'
          }
        >
          <Card.Header
            className={
              props.pdf ? 'border-bottom-0 pdf-card' : 'border-bottom-0'
            }
          >
            <div className="text-header">{props.title}</div>
          </Card.Header>
          <Card.Body>
            <CarAttribute
              title="Series"
              value={props.vehicle && props.vehicle.variant}
            />
            <CarAttribute
              title="Colour"
              value={props.vehicle && props.vehicle.bodyColour}
            />
            <CarAttribute
              title="Interior colour"
              value={props.vehicle && props.vehicle.trimColour}
            />
            <CarAttribute title="Engine" value={engineText} />
            <CarAttribute
              title="Transmission"
              value={props.vehicle && props.vehicle.transmission}
            />
            <CarAttribute
              title="Body"
              value={props.vehicle && props.vehicle.style}
            />
            <CarAttribute
              title="Fuel type"
              value={capitalizeOnlyFirstLetter(
                props.vehicle && props.vehicle.fuelType
              )}
            />
            <CarAttribute
              title="Cylinders"
              value={props.vehicle && props.vehicle.vehicleCylinderConfig}
            />
            <CarAttribute
              title="Payload"
              value={
                props.vehicle && props.vehicle.payLoad == '0'
                  ? undefined
                  : props.vehicle.payLoad
              }
            />
            {cO2EmissionsPerKM != null && (
              <CarAttribute
                title={emissionAttribute}
                value={cO2EmissionsPerKM + ' g/km'}
              />
            )}
            {cO2EmissionsPerKM != null && (
              <S.EmissionContainer>
                <Emission value={cO2EmissionsPerKM} />
              </S.EmissionContainer>
            )}
          </Card.Body>
        </Card>
      </Container>
    </div>
  )
}

export default CarDetailsSection
